import type { User } from '../../api/users';
import Country from '../../components/CountryCodeDropDown/resources/country';
import type { CountryCode } from '../../components/CountryCodeDropDown/types';
import { getItem } from '../../utils/storage';
import { RegionEnum } from '../../config/region';
import { ELanguages } from '../../i18n';

const EPhoneCodes: Record<Exclude<ELanguages, ELanguages.en | 'en-TT'>, CountryCode> = {
  se: 'SE',
  fi: 'FI',
  nl: 'NL',
  pt: 'BR',
  es: 'MX',
} as const;

export const getEPhoneCodes = (language: ELanguages): CountryCode => {
  if (language === ELanguages.en) {
    const savedRegion = getItem('selectedRegion') || RegionEnum.SE;

    const isSwedishRegion = savedRegion === RegionEnum.SE || savedRegion === RegionEnum.EU;

    return isSwedishRegion ? 'SE' : 'BR';
  }

  if (language === ELanguages['en-TT']) {
    return 'BR';
  }
  return EPhoneCodes[language];
};

export type PhoneNumberShorten = {
  country?: CountryCode;
  nationalNumber?: string;
  countryCallingCode?: string;
};

export const isNumbersOnly = (text: string): boolean => {
  const numRegex = /^\d+$/;

  return numRegex.test(text);
};

export const getPhoneNumber = (phone: string): PhoneNumberShorten | undefined => {
  const trimmedPhone = phone.trim();
  const country = Country.getCountryByNumber(trimmedPhone);
  if (country) {
    const { dial_code: dialCode, code } = country;
    const phoneNumber = dialCode ? trimmedPhone.slice(dialCode.length) : trimmedPhone;
    return { country: code, countryCallingCode: dialCode, nationalNumber: phoneNumber };
  }
  return { country: '' as CountryCode, nationalNumber: trimmedPhone };
};

const getPhoneDataFromLocale = (locale: ELanguages) => {
  const phoneCodeByLocale = getEPhoneCodes(locale);
  const countryData = Country.getCountryDataByCode(phoneCodeByLocale);
  return getPhoneNumber(`${countryData?.dial_code}`);
};

export const getSecondaryPhoneNumber = (
  phoneNumbers: User['phoneNumbers'] | undefined,
  language: ELanguages
): PhoneNumberShorten | undefined => {
  const firstNumber = phoneNumbers?.primary;
  const secondNumber = phoneNumbers?.secondary;
  let phoneData;
  if (secondNumber) {
    phoneData = getPhoneNumber(secondNumber);
  } else if (firstNumber) {
    phoneData = getPhoneNumber(firstNumber);
    if (phoneData) phoneData.nationalNumber = '';
  } else {
    phoneData = getPhoneDataFromLocale(language);
  }
  return phoneData;
};

export const getPhoneNumberStringByPhoneNumberObject = (
  phoneNumber: PhoneNumberShorten | undefined
) => {
  if (!phoneNumber) {
    return '-';
  }
  const { countryCallingCode, nationalNumber } = phoneNumber;
  return countryCallingCode && nationalNumber ? `${countryCallingCode} ${nationalNumber}` : '-';
};
